export const REPORT_SECTION_IDS = [
    'performance.overview',
    'performance.keyMetrics',
    'analysis.publisher',
    'analysis.campaign',
] as const

export type ReportSectionId = typeof REPORT_SECTION_IDS[number]
export type ReportUserKind = 'advertiser' | 'publisher'

type LegacyReportSectionSource = {
    includePerformanceOverview?: boolean
    includeMonthlyPerformance?: boolean
    includeKeyMetrics?: boolean
}

const REPORT_SECTION_SET = new Set<string>(REPORT_SECTION_IDS)
const hasOwn = (value: object | undefined, key: string) => (
    !!value && Object.prototype.hasOwnProperty.call(value, key)
)

export function normalizeReportSections(
    value: unknown,
    userKind: ReportUserKind,
    legacySource?: LegacyReportSectionSource,
): ReportSectionId[] {
    const includeOverview = hasOwn(legacySource, 'includePerformanceOverview')
        ? legacySource?.includePerformanceOverview === true
        : legacySource?.includeMonthlyPerformance === true
    const candidates = Array.isArray(value)
        ? value
        : [
            ...(includeOverview ? ['performance.overview'] : []),
            ...(legacySource?.includeKeyMetrics === true ? ['performance.keyMetrics'] : []),
        ]

    return Array.from(new Set(candidates))
        .filter((section): section is ReportSectionId => (
            typeof section === 'string'
            && REPORT_SECTION_SET.has(section)
            && !(userKind === 'publisher' && section === 'analysis.publisher')
        ))
}

export function parseReportSectionsParam(
    value: string | null,
    userKind: ReportUserKind,
    legacySource?: LegacyReportSectionSource,
) {
    if (value === null) return normalizeReportSections(undefined, userKind, legacySource)
    return normalizeReportSections(value.split(',').filter(Boolean), userKind)
}

export function hasReportSection(sections: readonly ReportSectionId[] | undefined, section: ReportSectionId) {
    return sections?.includes(section) === true
}
